import { useState } from "react";

/* ── Theme ───────────────────────────────────────────────── */
const C = {
  accent:  "#1aff6e",
  accent2: "#00e55a",
  bg:      "#020d06",
  bg2:     "#041f0e",
  glass:   "rgba(8,40,18,0.65)",
  border:  "rgba(26,255,110,0.18)",
  border2: "rgba(26,255,110,0.08)",
  text:    "#d4fce3",
  text2:   "#6db880",
  text3:   "#2d6642",
  code:    "rgba(2,20,8,0.9)",
};

const BASE_URL = "https://dashboard.kickblaze.xyz/api/partner.php";

/* ── Tiny helpers ────────────────────────────────────────── */
const Tag = ({ children, color = C.accent }) => (
  <span style={{
    display:"inline-block", padding:"2px 10px", borderRadius:"20px", fontSize:"11px",
    fontWeight:700, letterSpacing:"0.06em",
    background: color === "green"  ? "rgba(26,255,110,0.12)"  :
                color === "yellow" ? "rgba(255,200,0,0.12)"   :
                color === "red"    ? "rgba(255,60,80,0.12)"   :
                                     "rgba(26,255,110,0.12)",
    color:       color === "green"  ? "#4dffa0"  :
                 color === "yellow" ? "#ffd54f"  :
                 color === "red"    ? "#ff8a96"  :
                                      "#4dffa0",
    border: `1px solid ${
                color === "green"  ? "rgba(26,255,110,0.3)"  :
                color === "yellow" ? "rgba(255,200,0,0.3)"   :
                color === "red"    ? "rgba(255,60,80,0.3)"   :
                                     "rgba(26,255,110,0.3)"}`,
  }}>{children}</span>
);

const Badge = ({ method }) => (
  <span style={{
    padding:"3px 10px", borderRadius:"6px", fontSize:"11px", fontWeight:800,
    letterSpacing:"0.08em", marginRight:"10px",
    background:"rgba(26,255,110,0.15)", color:C.accent,
    border:`1px solid rgba(26,255,110,0.3)`,
  }}>POST</span>
);

/* ── Code block with copy ────────────────────────────────── */
function Code({ lang = "json", children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div style={{ position:"relative", marginBottom:"16px" }}>
      <div style={{
        background:C.code, border:`1px solid ${C.border2}`, borderRadius:"12px",
        padding:"16px 20px", fontFamily:"'JetBrains Mono','Fira Code',monospace",
        fontSize:"12px", lineHeight:"1.8", color:"#a0e8b0",
        overflowX:"auto", whiteSpace:"pre",
      }}>
        {/* Syntax-highlight JSON keys */}
        {lang === "json"
          ? children.trim().split("\n").map((line, i) => {
              const hl = line
                .replace(/"([^"]+)":/g, (_, k) => `<span style="color:#5af">"${k}"</span>:`)
                .replace(/: "(.*?)"/g, (_, v) => `: <span style="color:#a0e8b0">"${v}"</span>`)
                .replace(/: (true|false|null|\d+)/g, (_, v) =>
                  `: <span style="color:#ffd54f">${v}</span>`);
              return <div key={i} dangerouslySetInnerHTML={{ __html: hl }} />;
            })
          : children.trim().split("\n").map((line, i) => (
              <div key={i} style={{ color: line.startsWith("#") ? C.text3 : "#a0e8b0" }}>
                {line}
              </div>
            ))
        }
      </div>
      <button onClick={copy} style={{
        position:"absolute", top:"10px", right:"10px",
        background:"rgba(26,255,110,0.1)", border:`1px solid ${C.border}`,
        borderRadius:"8px", padding:"4px 10px", color:C.text2,
        fontSize:"10px", fontWeight:700, cursor:"pointer", fontFamily:"inherit",
        letterSpacing:"0.06em", transition:"all 0.2s",
      }}>
        {copied ? "✓ Copied" : "Copy"}
      </button>
    </div>
  );
}

/* ── Param table ─────────────────────────────────────────── */
function ParamTable({ rows }) {
  return (
    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"13px", marginBottom:"16px" }}>
      <thead>
        <tr>{["Field","Type","Required","Description"].map(h => (
          <th key={h} style={{
            textAlign:"left", padding:"8px 12px", color:C.text3, fontSize:"10px",
            fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
            borderBottom:`1px solid ${C.border2}`,
          }}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {rows.map(([field, type, req, desc], i) => (
          <tr key={i} style={{ borderBottom:`1px solid ${C.border2}` }}>
            <td style={{ padding:"9px 12px" }}>
              <code style={{ background:"rgba(26,255,110,0.08)", padding:"2px 8px",
                borderRadius:"6px", fontSize:"12px", color:C.accent, fontFamily:"monospace" }}>
                {field}
              </code>
            </td>
            <td style={{ padding:"9px 12px", color:C.text3, fontFamily:"monospace", fontSize:"12px" }}>{type}</td>
            <td style={{ padding:"9px 12px" }}>
              <Tag color={req === "Yes" ? "green" : "yellow"}>{req}</Tag>
            </td>
            <td style={{ padding:"9px 12px", color:C.text2, lineHeight:1.6 }}>{desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── Section card ────────────────────────────────────────── */
function Section({ id, icon, title, badge, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div id={id} style={{
      background:C.glass, backdropFilter:"blur(20px)",
      border:`1px solid ${C.border}`, borderRadius:"18px",
      marginBottom:"20px", overflow:"hidden",
    }}>
      <div onClick={() => setOpen(o => !o)} style={{
        padding:"18px 24px", cursor:"pointer",
        display:"flex", alignItems:"center", gap:"12px",
        borderBottom: open ? `1px solid ${C.border2}` : "none",
        background:"rgba(26,255,110,0.03)",
      }}>
        <span style={{ fontSize:"20px" }}>{icon}</span>
        <div style={{ flex:1 }}>
          <div style={{
            fontFamily:"'Plus Jakarta Sans',sans-serif",
            fontSize:"15px", fontWeight:800, color:C.text,
          }}>{title}</div>
          {badge && <div style={{ fontSize:"11px", color:C.text3, marginTop:"2px" }}>{badge}</div>}
        </div>
        <span style={{ color:C.text3, fontSize:"13px" }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && <div style={{ padding:"24px" }}>{children}</div>}
    </div>
  );
}

/* ── Endpoint block ──────────────────────────────────────── */
function Endpoint({ action, title, desc, params, request, response }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      border:`1px solid ${C.border2}`, borderRadius:"12px",
      marginBottom:"14px", overflow:"hidden",
    }}>
      <div onClick={() => setOpen(o => !o)} style={{
        padding:"14px 18px", cursor:"pointer",
        display:"flex", alignItems:"center", gap:"10px",
        background:"rgba(26,255,110,0.02)",
      }}>
        <Badge method="POST"/>
        <code style={{
          fontFamily:"monospace", fontSize:"13px", fontWeight:700,
          color:C.accent, flex:1,
        }}>{action}</code>
        <span style={{ color:C.text3, fontSize:"12px", marginRight:"8px" }}>{title}</span>
        <span style={{ color:C.text3, fontSize:"11px" }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ padding:"18px", borderTop:`1px solid ${C.border2}` }}>
          {desc && <p style={{ color:C.text2, fontSize:"13px", marginBottom:"16px", lineHeight:1.7 }}>{desc}</p>}
          {params && params.length > 0 && (<>
            <div style={{ fontSize:"11px", fontWeight:700, color:C.text3, textTransform:"uppercase",
              letterSpacing:"0.1em", marginBottom:"8px" }}>Parameters</div>
            <ParamTable rows={params}/>
          </>)}
          <div style={{ fontSize:"11px", fontWeight:700, color:C.text3, textTransform:"uppercase",
            letterSpacing:"0.1em", marginBottom:"8px" }}>Example Request</div>
          <Code lang="json">{request}</Code>
          <div style={{ fontSize:"11px", fontWeight:700, color:C.text3, textTransform:"uppercase",
            letterSpacing:"0.1em", marginBottom:"8px" }}>Example Response</div>
          <Code lang="json">{response}</Code>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN DOCS PAGE
───────────────────────────────────────────────────────── */
export default function ApiDocs() {
  const [activeSection, setActiveSection] = useState(null);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior:"smooth", block:"start" });
    setActiveSection(id);
  };

  const navItems = [
    { id:"overview",       label:"Overview",         icon:"📖" },
    { id:"auth",           label:"Authentication",   icon:"🔐" },
    { id:"errors",         label:"Error Handling",   icon:"⚠️" },
    { id:"rate-limits",    label:"Rate Limits",      icon:"⏱️" },
    { id:"partner-info",   label:"partner.info",     icon:"ℹ️" },
    { id:"user-create",    label:"user.create",      icon:"👤" },
    { id:"user-auth",      label:"user.auth",        icon:"🔑" },
    { id:"user-list",      label:"user.list",        icon:"📋" },
    { id:"user-get",       label:"user.get",         icon:"🔍" },
    { id:"user-update",    label:"user.update",      icon:"✏️" },
    { id:"user-delete",    label:"user.delete",      icon:"🗑️" },
    { id:"task-create",    label:"task.create",      icon:"⚡" },
    { id:"task-list",      label:"task.list",        icon:"📋" },
    { id:"task-update",    label:"task.update",      icon:"✏️" },
    { id:"task-delete",    label:"task.delete",      icon:"🗑️" },
    { id:"task-status",    label:"task.status",      icon:"📡" },
    { id:"example-php",    label:"PHP Example",      icon:"🐘" },
    { id:"example-python", label:"Python Example",   icon:"🐍" },
    { id:"example-js",     label:"JavaScript",       icon:"🌐" },
  ];

  return (
    <div style={{
      minHeight:"100vh",
      background:`radial-gradient(ellipse at 20% 10%, ${C.bg2} 0%, ${C.bg} 60%)`,
      fontFamily:"'DM Sans','Segoe UI',sans-serif",
      color:C.text,
    }}>
      {/* Header */}
      <div style={{
        background:"rgba(2,10,4,0.95)", backdropFilter:"blur(20px)",
        borderBottom:`1px solid ${C.border2}`, padding:"0 32px",
        position:"sticky", top:0, zIndex:100,
        display:"flex", alignItems:"center", gap:"20px", height:"60px",
      }}>
        <div style={{
          fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:900,
          fontSize:"18px", color:C.accent, letterSpacing:"-0.02em",
        }}>KickBlaze</div>
        <div style={{
          width:"1px", height:"24px", background:C.border2,
        }}/>
        <div style={{ fontSize:"14px", fontWeight:600, color:C.text2 }}>
          Partner API Documentation
        </div>
        <div style={{ flex:1 }}/>
        <Tag color="green">v1.0</Tag>
        <a href="https://dashboard.kickblaze.xyz" target="_blank"
           rel="noopener noreferrer"
           style={{ textDecoration:"none" }}>
          <button style={{
            background:"rgba(26,255,110,0.1)", border:`1px solid ${C.border}`,
            borderRadius:"20px", padding:"6px 16px", color:C.accent,
            fontSize:"12px", fontWeight:700, cursor:"pointer", fontFamily:"inherit",
          }}>← Dashboard</button>
        </a>
      </div>

      <div style={{ display:"flex", maxWidth:"1200px", margin:"0 auto", padding:"32px 24px", gap:"28px" }}>

        {/* Sidebar nav */}
        <div style={{
          width:"210px", flexShrink:0, position:"sticky",
          top:"80px", height:"fit-content",
        }}>
          <div style={{
            background:C.glass, backdropFilter:"blur(20px)",
            border:`1px solid ${C.border}`, borderRadius:"16px",
            padding:"12px 8px",
          }}>
            {navItems.map(({ id, label, icon }) => (
              <div key={id} onClick={() => scrollTo(id)} style={{
                padding:"8px 12px", borderRadius:"10px", cursor:"pointer",
                fontSize:"12px", fontWeight:600, color:activeSection===id ? C.accent : C.text3,
                background:activeSection===id ? "rgba(26,255,110,0.08)" : "transparent",
                display:"flex", alignItems:"center", gap:"8px",
                transition:"all 0.15s", marginBottom:"2px",
              }}>
                <span style={{ fontSize:"13px" }}>{icon}</span>
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex:1, minWidth:0 }}>

          {/* Overview */}
          <Section id="overview" icon="📖" title="Overview"
            badge={`Endpoint: ${BASE_URL}`}>
            <p style={{ color:C.text2, lineHeight:1.8, marginBottom:"16px" }}>
              The KickBlaze Partner API allows approved third-party panels and services to
              manage users and viewer tasks within the KickBlaze ecosystem. Your clients
              register and run tasks through your interface — everything is stored in
              KickBlaze's infrastructure.
            </p>
            <div style={{
              display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"12px",
            }}>
              {[
                ["🔒 Isolated",    "You only see your own users and tasks"],
                ["⚡ Real-time",   "Task status pulled live from the proxy"],
                ["📦 Budget-safe", "Viewer limits enforced server-side"],
              ].map(([t, d]) => (
                <div key={t} style={{
                  background:"rgba(26,255,110,0.04)",
                  border:`1px solid ${C.border2}`,
                  borderRadius:"12px", padding:"16px",
                }}>
                  <div style={{ fontWeight:700, color:C.text, marginBottom:"6px", fontSize:"13px" }}>{t}</div>
                  <div style={{ color:C.text3, fontSize:"12px", lineHeight:1.6 }}>{d}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* Auth */}
          <Section id="auth" icon="🔐" title="Authentication">
            <p style={{ color:C.text2, lineHeight:1.8, marginBottom:"16px" }}>
              Every request must include your secret key as an HTTP header. Keep this key
              private — do not expose it in client-side JavaScript or public repositories.
            </p>
            <Code lang="http">{`POST ${BASE_URL}
Content-Type: application/json
X-API-Key: your_64_character_secret_key_here`}</Code>
            <div style={{
              background:"rgba(255,185,0,0.06)", border:"1px solid rgba(255,185,0,0.2)",
              borderRadius:"12px", padding:"14px 18px",
            }}>
              <span style={{ color:"#ffd54f", fontWeight:700, fontSize:"13px" }}>⚠️ Security note: </span>
              <span style={{ color:C.text2, fontSize:"13px" }}>
                Always make API calls from your server, never from a browser.
                If your key is compromised, contact KickBlaze support to have it rotated immediately.
              </span>
            </div>
          </Section>

          {/* Errors */}
          <Section id="errors" icon="⚠️" title="Error Handling">
            <p style={{ color:C.text2, lineHeight:1.8, marginBottom:"16px" }}>
              All responses are JSON. Check the <code style={{ color:C.accent, fontFamily:"monospace" }}>success</code> field first.
              On failure, <code style={{ color:C.accent, fontFamily:"monospace" }}>error</code> contains a human-readable message.
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
              <div>
                <div style={{ fontSize:"11px", color:C.text3, fontWeight:700, textTransform:"uppercase",
                  letterSpacing:"0.1em", marginBottom:"8px" }}>Success</div>
                <Code lang="json">{`{
  "success": true,
  "username": "john_doe",
  "viewers": 100
}`}</Code>
              </div>
              <div>
                <div style={{ fontSize:"11px", color:C.text3, fontWeight:700, textTransform:"uppercase",
                  letterSpacing:"0.1em", marginBottom:"8px" }}>Error</div>
                <Code lang="json">{`{
  "success": false,
  "error": "Username already exists."
}`}</Code>
              </div>
            </div>
            <ParamTable rows={[
              ["success", "bool",   "Always", "true on success, false on any error"],
              ["error",   "string", "On fail", "Human-readable description of what went wrong"],
            ]}/>
          </Section>

          {/* Rate limits */}
          <Section id="rate-limits" icon="⏱️" title="Rate Limits">
            <p style={{ color:C.text2, lineHeight:1.8, marginBottom:"16px" }}>
              By default, each partner key is limited to <strong style={{ color:C.text }}>60 requests per minute</strong>.
              Exceeding this returns HTTP 429. Limits can be adjusted by contacting KickBlaze support.
            </p>
            <Code lang="json">{`{
  "success": false,
  "error": "Rate limit exceeded (60 requests/min). Slow down."
}`}</Code>
          </Section>

          {/* partner.info */}
          <Section id="partner-info" icon="ℹ️" title="Account">
            <Endpoint
              action="partner.info"
              title="Partner info & usage stats"
              desc="Returns details about your partner account, including total user and task counts."
              params={[]}
              request={`{
  "action": "partner.info"
}`}
              response={`{
  "success": true,
  "partner": "YourCompanyName",
  "partner_id": 1,
  "user_count": 42,
  "task_count": 87,
  "rate_limit": 60
}`}
            />
          </Section>

          {/* USER ENDPOINTS */}
          <Section id="user-create" icon="👤" title="User Management">

            <Endpoint
              action="user.create"
              title="Create a new user"
              desc="Registers a new user under your partner account. The user's IP is set to RESET so they can log in from any IP on first login."
              params={[
                ["username",  "string", "Yes", "Unique username (min 3 chars)"],
                ["password",  "string", "Yes", "Password (min 6 chars)"],
                ["email",     "string", "Yes", "Valid email address"],
                ["viewers",   "int",    "No",  "Viewer limit (default 0)"],
                ["chatters",  "int",    "No",  "Chatter limit (defaults to viewers)"],
                ["channels",  "int",    "No",  "Max channels (default 1)"],
                ["days",      "int",    "No",  "Subscription length in days (default 0 = inactive)"],
              ]}
              request={`{
  "action": "user.create",
  "username": "john_doe",
  "password": "securepass123",
  "email": "john@example.com",
  "viewers": 100,
  "channels": 2,
  "days": 30
}`}
              response={`{
  "success": true,
  "username": "john_doe",
  "email": "john@example.com",
  "viewers": 100,
  "channels": 2,
  "expires": "2025-08-12 23:59:59"
}`}
            />

            <Endpoint
              id="user-auth"
              action="user.auth"
              title="Authenticate a user"
              desc="Verifies a username and password. Use this on your login page — if success is true, create your own session for the user."
              params={[
                ["username", "string", "Yes", "The user's username"],
                ["password", "string", "Yes", "The user's password (plaintext — sent over HTTPS only)"],
              ]}
              request={`{
  "action": "user.auth",
  "username": "john_doe",
  "password": "securepass123"
}`}
              response={`{
  "success": true,
  "username": "john_doe",
  "email": "john@example.com",
  "viewers": 100,
  "chatters": 100,
  "channels": 2,
  "expires": "2025-08-12 23:59:59",
  "active": true
}`}
            />

            <Endpoint
              id="user-list"
              action="user.list"
              title="List all users"
              desc="Returns paginated list of all users under your partner account."
              params={[
                ["limit",  "int", "No", "Max results to return (default 100, max 500)"],
                ["offset", "int", "No", "Pagination offset (default 0)"],
              ]}
              request={`{
  "action": "user.list",
  "limit": 50,
  "offset": 0
}`}
              response={`{
  "success": true,
  "total": 42,
  "users": [
    {
      "username": "john_doe",
      "email": "john@example.com",
      "viewers": 100,
      "channels": 2,
      "expires": "2025-08-12 23:59:59",
      "active": true,
      "banned": false,
      "lastlogin": "2025-07-10 14:22:00"
    }
  ]
}`}
            />

            <Endpoint
              id="user-get"
              action="user.get"
              title="Get a single user"
              desc="Returns full details for one user. Only works if the user belongs to your partner account."
              params={[
                ["username", "string", "Yes", "The user's username (case-insensitive)"],
              ]}
              request={`{
  "action": "user.get",
  "username": "john_doe"
}`}
              response={`{
  "success": true,
  "username": "john_doe",
  "email": "john@example.com",
  "viewers": 100,
  "chatters": 100,
  "channels": 2,
  "expires": "2025-08-12 23:59:59",
  "active": true,
  "banned": false,
  "lastlogin": "2025-07-10 14:22:00"
}`}
            />

            <Endpoint
              id="user-update"
              action="user.update"
              title="Update a user's plan"
              desc="Updates one or more plan fields. Only fields you include are changed. Use days to add time on top of existing subscription, or expires to set an exact date."
              params={[
                ["username", "string", "Yes", "The user's username"],
                ["viewers",  "int",    "No",  "New viewer limit"],
                ["chatters", "int",    "No",  "New chatter limit"],
                ["channels", "int",    "No",  "New channel limit"],
                ["days",     "int",    "No",  "Days to add to current subscription"],
                ["expires",  "string", "No",  "Set exact expiry date: YYYY-MM-DD"],
                ["banned",   "bool",   "No",  "true to ban, false to unban"],
              ]}
              request={`{
  "action": "user.update",
  "username": "john_doe",
  "viewers": 200,
  "days": 14
}`}
              response={`{
  "success": true,
  "username": "john_doe",
  "viewers": 200,
  "channels": 2,
  "expires": "2025-08-26 23:59:59",
  "banned": false
}`}
            />

            <Endpoint
              id="user-delete"
              action="user.delete"
              title="Delete (ban) a user"
              desc="Soft-deletes a user by setting their banned flag. Their data is preserved in the database."
              params={[
                ["username", "string", "Yes", "The user's username"],
              ]}
              request={`{
  "action": "user.delete",
  "username": "john_doe"
}`}
              response={`{
  "success": true,
  "deleted": "john_doe"
}`}
            />
          </Section>

          {/* TASK ENDPOINTS */}
          <Section id="task-create" icon="⚡" title="Task Management">

            <Endpoint
              action="task.create"
              title="Create a viewer task"
              desc="Creates a new task for a channel. Budget is enforced — the user's total active task viewers cannot exceed their plan limit. The task starts in 'stopped' state."
              params={[
                ["username",         "string", "Yes", "Username the task belongs to"],
                ["channel",          "string", "Yes", "Kick.com channel name (lowercase)"],
                ["viewers",          "int",    "Yes", "Number of viewers to send"],
                ["minDelay",         "int",    "No",  "Min message delay in ms (default 5000)"],
                ["maxDelay",         "int",    "No",  "Max message delay in ms (default 30000)"],
                ["sendViewers",      "bool",   "No",  "Send viewers (default true)"],
                ["sendMessages",     "bool",   "No",  "Send chat messages (default false)"],
                ["useProxies",       "bool",   "No",  "Use custom proxies (default false)"],
                ["useMessages",      "bool",   "No",  "Use custom message list (default false)"],
                ["dripViewers",      "bool",   "No",  "Drip viewers in gradually (default false)"],
                ["dripMs",           "int",    "No",  "Total drip time in ms (default 1200000)"],
                ["killViewers",      "bool",   "No",  "Enable viewer fluctuation (default false)"],
                ["killPct",          "int",    "No",  "Kill percentage for fluctuation (default 2)"],
                ["messagesFilePath", "string", "No",  "Paste link for custom messages"],
              ]}
              request={`{
  "action": "task.create",
  "username": "john_doe",
  "channel": "shroud",
  "viewers": 50,
  "sendViewers": true,
  "sendMessages": false
}`}
              response={`{
  "success": true,
  "task_id": 142,
  "channel": "shroud",
  "viewers": 50
}`}
            />

            <Endpoint
              id="task-list"
              action="task.list"
              title="List tasks"
              desc="Returns tasks across your partner account. Optionally filter to a specific user."
              params={[
                ["username", "string", "No", "Filter tasks to this user only"],
                ["limit",    "int",    "No", "Max results (default 100, max 500)"],
                ["offset",   "int",    "No", "Pagination offset (default 0)"],
              ]}
              request={`{
  "action": "task.list",
  "username": "john_doe"
}`}
              response={`{
  "success": true,
  "tasks": [
    {
      "id": 142,
      "username": "john_doe",
      "channel": "shroud",
      "viewers": 50,
      "status": "stopped",
      "is_live": false,
      "created_at": "2025-07-10 12:00:00",
      "send_viewers": true,
      "send_messages": false,
      "drip_viewers": false,
      "kill_viewers": false
    }
  ]
}`}
            />

            <Endpoint
              id="task-update"
              action="task.update"
              title="Update a task"
              desc="Updates task settings. Only fields you include are changed. Budget is re-validated if you change the viewer count."
              params={[
                ["task_id",          "int",    "Yes", "The task ID to update"],
                ["viewers",          "int",    "No",  "New viewer count (budget enforced)"],
                ["minDelay",         "int",    "No",  "Min message delay in ms"],
                ["maxDelay",         "int",    "No",  "Max message delay in ms"],
                ["sendViewers",      "bool",   "No",  "Send viewers"],
                ["sendMessages",     "bool",   "No",  "Send chat messages"],
                ["useProxies",       "bool",   "No",  "Use custom proxies"],
                ["useMessages",      "bool",   "No",  "Use custom messages"],
                ["dripViewers",      "bool",   "No",  "Drip viewers"],
                ["dripMs",           "int",    "No",  "Drip time in ms"],
                ["killViewers",      "bool",   "No",  "Kill/fluctuate viewers"],
                ["killPct",          "int",    "No",  "Kill percentage"],
                ["messagesFilePath", "string", "No",  "Custom messages paste URL"],
              ]}
              request={`{
  "action": "task.update",
  "task_id": 142,
  "viewers": 75,
  "sendMessages": true
}`}
              response={`{
  "success": true,
  "task_id": 142,
  "viewers": 75
}`}
            />

            <Endpoint
              id="task-delete"
              action="task.delete"
              title="Delete a task"
              desc="Permanently removes a task. Make sure to stop it first if it is running."
              params={[
                ["task_id", "int", "Yes", "The task ID to delete"],
              ]}
              request={`{
  "action": "task.delete",
  "task_id": 142
}`}
              response={`{
  "success": true,
  "deleted": 142
}`}
            />

            <Endpoint
              id="task-status"
              action="task.status"
              title="Get live task status"
              desc="Returns the current status and live thread count from the KickBlaze proxy system."
              params={[
                ["task_id", "int", "Yes", "The task ID to check"],
              ]}
              request={`{
  "action": "task.status",
  "task_id": 142
}`}
              response={`{
  "success": true,
  "task_id": 142,
  "channel": "shroud",
  "status": "running",
  "is_live": true,
  "live_threads": 47,
  "viewers_set": 50
}`}
            />
          </Section>

          {/* PHP Example */}
          <Section id="example-php" icon="🐘" title="PHP Example" badge="Full working example">
            <p style={{ color:C.text2, fontSize:"13px", lineHeight:1.8, marginBottom:"16px" }}>
              Save as <code style={{ color:C.accent, fontFamily:"monospace" }}>kickblaze.php</code> and
              require it anywhere in your project.
            </p>
            <Code lang="php">{`<?php
class KickBlazeAPI {
    private string $key;
    private string $base = 'https://dashboard.kickblaze.xyz/api/partner.php';

    public function __construct(string $apiKey) {
        $this->key = $apiKey;
    }

    private function call(array $body): array {
        $ch = curl_init($this->base);
        curl_setopt_array($ch, [
            CURLOPT_RETURNTRANSFER => true,
            CURLOPT_TIMEOUT        => 15,
            CURLOPT_POST           => true,
            CURLOPT_POSTFIELDS     => json_encode($body),
            CURLOPT_HTTPHEADER     => [
                'Content-Type: application/json',
                'X-API-Key: ' . $this->key,
            ],
        ]);
        $res = curl_exec($ch);
        curl_close($ch);
        return json_decode($res, true) ?? ['success' => false, 'error' => 'curl failed'];
    }

    /* Users */
    public function createUser(string $u, string $pw, string $email,
                               int $viewers = 0, int $days = 0): array {
        return $this->call([
            'action'  => 'user.create',
            'username'=> $u, 'password'=> $pw, 'email'=> $email,
            'viewers' => $viewers, 'days' => $days,
        ]);
    }

    public function authUser(string $u, string $pw): array {
        return $this->call(['action'=>'user.auth','username'=>$u,'password'=>$pw]);
    }

    public function getUser(string $u): array {
        return $this->call(['action'=>'user.get','username'=>$u]);
    }

    public function updateUser(string $u, array $fields): array {
        return $this->call(array_merge(['action'=>'user.update','username'=>$u], $fields));
    }

    /* Tasks */
    public function createTask(string $u, string $channel, int $viewers,
                               array $opts = []): array {
        return $this->call(array_merge([
            'action'  => 'task.create',
            'username'=> $u, 'channel'=> $channel, 'viewers'=> $viewers,
        ], $opts));
    }

    public function listTasks(string $u = ''): array {
        $body = ['action'=>'task.list'];
        if ($u) $body['username'] = $u;
        return $this->call($body);
    }

    public function taskStatus(int $taskId): array {
        return $this->call(['action'=>'task.status','task_id'=>$taskId]);
    }
}

/* Usage */
$api = new KickBlazeAPI('your_key_here');

// Create a user
$user = $api->createUser('testuser', 'pass1234', 'test@example.com', 100, 30);

// Start a task
$task = $api->createTask('testuser', 'channelname', 50, ['sendViewers'=>true]);

// Check live status
$status = $api->taskStatus($task['task_id']);
echo "Live threads: " . $status['live_threads'];`}</Code>
          </Section>

          {/* Python Example */}
          <Section id="example-python" icon="🐍" title="Python Example">
            <Code lang="python">{`import requests

class KickBlazeAPI:
    BASE = "https://dashboard.kickblaze.xyz/api/partner.php"

    def __init__(self, api_key: str):
        self.headers = {
            "Content-Type": "application/json",
            "X-API-Key": api_key,
        }

    def _call(self, body: dict) -> dict:
        r = requests.post(self.BASE, json=body, headers=self.headers, timeout=15)
        r.raise_for_status()
        return r.json()

    # Users
    def create_user(self, username, password, email, viewers=0, days=0):
        return self._call({
            "action": "user.create",
            "username": username, "password": password,
            "email": email, "viewers": viewers, "days": days,
        })

    def auth_user(self, username, password):
        return self._call({"action":"user.auth","username":username,"password":password})

    def update_user(self, username, **kwargs):
        return self._call({"action":"user.update","username":username,**kwargs})

    # Tasks
    def create_task(self, username, channel, viewers, **opts):
        return self._call({
            "action":"task.create","username":username,
            "channel":channel,"viewers":viewers,**opts
        })

    def list_tasks(self, username=None):
        body = {"action":"task.list"}
        if username: body["username"] = username
        return self._call(body)

    def task_status(self, task_id: int):
        return self._call({"action":"task.status","task_id":task_id})

# Usage
api = KickBlazeAPI("your_key_here")

# Create user with 30-day plan and 100 viewers
user = api.create_user("testuser", "pass1234", "test@example.com",
                        viewers=100, days=30)
print(user)

# Create a task
task = api.create_task("testuser", "channelname", 50, sendViewers=True)
print(f"Task ID: {task['task_id']}")

# Poll status
import time
for _ in range(5):
    status = api.task_status(task["task_id"])
    print(f"Live threads: {status.get('live_threads', 0)}")
    time.sleep(10)`}</Code>
          </Section>

          {/* JavaScript Example */}
          <Section id="example-js" icon="🌐" title="JavaScript / Node.js Example">
            <p style={{ color:"#ff8a96", fontSize:"13px", marginBottom:"14px",
              background:"rgba(255,60,80,0.08)", border:"1px solid rgba(255,60,80,0.2)",
              borderRadius:"10px", padding:"12px 16px" }}>
              ⚠️ Never use your API key in client-side (browser) JavaScript. This example
              is for Node.js server-side use only.
            </p>
            <Code lang="js">{`// kickblaze.js — Node.js
const fetch = require('node-fetch'); // npm i node-fetch

class KickBlazeAPI {
  constructor(apiKey) {
    this.key  = apiKey;
    this.base = 'https://dashboard.kickblaze.xyz/api/partner.php';
  }

  async call(body) {
    const res = await fetch(this.base, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key':    this.key,
      },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // Users
  createUser(username, password, email, viewers = 0, days = 0) {
    return this.call({ action:'user.create', username, password, email, viewers, days });
  }
  authUser(username, password) {
    return this.call({ action:'user.auth', username, password });
  }
  updateUser(username, fields) {
    return this.call({ action:'user.update', username, ...fields });
  }

  // Tasks
  createTask(username, channel, viewers, opts = {}) {
    return this.call({ action:'task.create', username, channel, viewers, ...opts });
  }
  listTasks(username = null) {
    const body = { action:'task.list' };
    if (username) body.username = username;
    return this.call(body);
  }
  taskStatus(taskId) {
    return this.call({ action:'task.status', task_id: taskId });
  }
}

// Usage
const api = new KickBlazeAPI('your_key_here');

(async () => {
  // Create a user
  const user = await api.createUser('testuser', 'pass1234', 'test@example.com', 100, 30);
  console.log('Created:', user);

  // Create a task
  const task = await api.createTask('testuser', 'channelname', 50, { sendViewers: true });
  console.log('Task ID:', task.task_id);

  // Check status
  const status = await api.taskStatus(task.task_id);
  console.log('Live threads:', status.live_threads);
})();`}</Code>
          </Section>

          {/* Footer */}
          <div style={{
            textAlign:"center", padding:"32px 0 16px",
            color:C.text3, fontSize:"12px",
          }}>
            KickBlaze Partner API v1.0 — For support, contact us on Discord.
          </div>

        </div>
      </div>
    </div>
  );
}
